import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './BondsGrid.module.css';
import BondCard from './BondCard';
import Loader from '../Loader';

interface Bond {
  _id: string;
  id_issuer: string;
  isin: string;
  issuer_name: string;
  title?: string;
  issue_date?: string;
  maturity_date?: string;
  currency?: string;
  volume_issued?: number;
  coupon_rate?: number;
  green_bond?: boolean;
  social_bond?: boolean;
  sustainability_bond?: boolean;
  sustainability_linked_bond?: boolean;
  sdg_numbers?: number[];
  tags?: string[];
  public?: boolean;
}

interface BondsGridProps {
  bonds: Bond[];
  loading?: boolean;
  onTagClick?: (tag: string) => void;
  onSDGClick?: (sdgNumber: number) => void;
  emptyMessage?: string;
  className?: string;
  viewMode?: 'grid' | 'list';
}

/**
 * BondsGrid - Responsive grid layout for displaying bond cards
 * Supports both grid and list view modes with smooth animations
 */
const BondsGrid: React.FC<BondsGridProps> = ({
  bonds,
  loading = false,
  onTagClick,
  onSDGClick,
  emptyMessage,
  className = '',
  viewMode = 'grid'
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className={styles.loaderContainer}>
        <Loader />
        <p className={styles.loadingText}>
          {t('bonds.loading', 'Loading ...')}
        </p>
      </div>
    );
  }

  if (!bonds || bonds.length === 0) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon}>📊</div>
        <h3 className={styles.emptyTitle}>
          {t('bonds.empty.title', 'No bonds found')}
        </h3>
        <p className={styles.emptyMessage}>
          {emptyMessage || t('bonds.empty.message', 'Try adjusting your filters or search criteria.')}
        </p>
      </div>
    );
  }

  // Map viewMode to the appropriate CSS class
  const modeClass = viewMode === 'grid' ? styles.gridMode : styles.listMode;

  return (
    <div className={`${modeClass} ${className}`}>
      <div className={styles.itemsContainer}>
        {bonds.map((bond, index) => (
          <div
            key={bond._id}
            className={styles.gridItem}
            style={{
              animationDelay: `${Math.min(index * 50, 500)}ms`
            }}
          >
            <BondCard
              bond={bond}
              onTagClick={onTagClick}
              onSDGClick={onSDGClick}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default BondsGrid;